/**
 * driveAutoBackup — Sprint 4C
 * Automatic, client-side backups to the user's Google Drive. Two files, two jobs
 * (so a frequent light save never clobbers the photos of a full one):
 *
 *   • wayfare-text.json  — TEXT ONLY (notes, lists, budget, itinerary). Light (KB),
 *     uploaded frequently on ANY connection: debounced after edits, when the app
 *     goes to background, and when connectivity returns. Roaming-safe.
 *   • wayfare-backup.json — FULL (with photos). Manual 💾 anytime, plus a SCHEDULED
 *     run: daily during the trip dates, weekly otherwise — and ONLY on Wi-Fi (the
 *     full one is heavy; on mobile data it waits, the light text file keeps things
 *     fresh meanwhile).
 *
 * No server cron is possible (the data lives in the browser, never on our server),
 * so scheduling is OPPORTUNISTIC: checks run while the app is open/active. Each
 * file is upserted by name (overwritten) — no accumulation.
 */
import autoBackupManager from './autoBackupManager';
import * as googleDrive from './googleDrive';
import storageManager from './storageManager';
import { notifyDone } from './notify';

export const TEXT_FILE = 'wayfare-text.json';
export const FULL_FILE = 'wayfare-backup.json';
const FULL_LAST_KEY = 'wf_drive_full_last';
const TEXT_DEBOUNCE_MS = 2 * 60 * 1000;   // 2 min after the last edit
const DAY_MS = 24 * 60 * 60 * 1000;

let _init = false;
let _textTimer = null;
let _textBusy = false;
let _fullBusy = false;

/** Best-effort Wi-Fi detection. Unknown (e.g. iOS) → treated as NOT Wi-Fi, so the
 *  heavy full backup won't run on metered data. Manual 💾 always works. */
function isWifi() {
  const c = (typeof navigator !== 'undefined') && (navigator.connection || navigator.webkitConnection);
  return !!(c && c.type === 'wifi');
}

function withinTripDates(trip) {
  const s = trip?.dates?.startDate, e = trip?.dates?.endDate;
  if (!s || !e) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(s); start.setHours(0, 0, 0, 0);
  const end = new Date(e); end.setHours(23, 59, 59, 999);
  return today >= start && today <= end;
}

/** Light, frequent text-only backup → wayfare-text.json. Any connection. */
async function runTextBackup() {
  if (_textBusy || !googleDrive.isConnected()) return;
  _textBusy = true;
  try {
    const backup = await autoBackupManager.createBackupWithoutPhotos();
    await googleDrive.uploadBackup(backup, TEXT_FILE);
  } catch { /* best-effort */ } finally { _textBusy = false; }
}

function scheduleTextBackup() {
  if (!googleDrive.isConnected()) return;
  if (_textTimer) clearTimeout(_textTimer);
  _textTimer = setTimeout(runTextBackup, TEXT_DEBOUNCE_MS);
}

/** Full backup (with photos) → wayfare-backup.json, only when due AND on Wi-Fi. */
async function maybeFullBackup() {
  if (_fullBusy || !googleDrive.isConnected()) return;
  if (!isWifi()) return;                                   // heavy → Wi-Fi only
  const trip = await storageManager.getActiveTrip().catch(() => null);
  const intervalMs = (withinTripDates(trip) ? 1 : 7) * DAY_MS;
  const last = parseInt(localStorage.getItem(FULL_LAST_KEY) || '0', 10);
  if (last && (Date.now() - last) < intervalMs) return;    // not due yet
  const changed = await autoBackupManager.hasUnsavedChanges().catch(() => true);
  if (last && !changed) return;                            // nothing new

  _fullBusy = true;
  try {
    const backup = await autoBackupManager.createBackup();
    await googleDrive.uploadBackup(backup, FULL_FILE);
    autoBackupManager.markBackedUp();
    localStorage.setItem(FULL_LAST_KEY, Date.now().toString());
    notifyDone('wf-auto-full', 'Wayfare', 'Copia de seguridad con fotos guardada en tu Drive');
  } catch { /* best-effort */ } finally { _fullBusy = false; }
}

/** Flush text now + check the scheduled full. Called on background/online/init. */
function tick() {
  runTextBackup();
  maybeFullBackup();
}

/** Wire up the triggers once, from app init. */
export function initDriveAutoBackup() {
  if (_init || typeof window === 'undefined') return;
  _init = true;
  window.addEventListener('wayfare-trip-saved', scheduleTextBackup);   // debounced after edits
  window.addEventListener('online', tick);                              // connectivity back
  document.addEventListener('visibilitychange', () => { if (document.hidden) tick(); }); // leaving app
  setTimeout(tick, 8000);                                               // opportunistic on open
}

/** Mark the full backup as just done (e.g. after a manual 💾) so the scheduler
 *  doesn't immediately repeat it. */
export function noteFullBackupDone() {
  try { localStorage.setItem(FULL_LAST_KEY, Date.now().toString()); } catch { /* ignore */ }
}
